import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, ListTodo, Radar, ListChecks } from 'lucide-react'
import { usePersistentToggle } from '../hooks/usePersistentToggle'
import { useDeskLens } from '../hooks/useDeskLens'
import { CreateMeetingModal, MeetingCreationData } from '../components/meeting/CreateMeetingModal'
import AddTaskModal from '../components/tasks/AddTaskModal'
import SecondaryNav from '../components/layout/SecondaryNav'
import { Canvas } from '../components/canvas/Canvas'
import ChangeRadar from '../components/dashboard/ChangeRadar'
import { MorningDigest } from '../components/canvas/widgets/MorningDigest'
import DoColumn from '../components/dashboard/DoColumn'
import FirstRunCard from '../components/dashboard/FirstRunCard'
import { InputDock } from '../components/canvas/InputDock'
import { FocusOverlayHost } from '../components/canvas/FocusOverlayHost'
import { useCanvasStore } from '../stores/canvasStore'
// TASK-667: live bot meetings PUSH a monitor tile onto the canvas — the RM is inside
// Teams and must never be navigated out of the call to see it.
import { useLiveMeetingSurfacing } from '../hooks/useLiveMeetingSurfacing'
import { httpClient } from '../lib/httpClient'
import { useAccount } from '../contexts/AccountContext'
import { taskService } from '../services/taskService'
import { TaskCreateRequest } from '../types/tasks'
import { toast } from 'sonner'

// TASK-575 "Two-Box Today": the dashboard IS the generative-UI workbench, now three regions —
// SecondaryNav (top) · NOTICE = ChangeRadar + Alerts tab (left) · CANVAS (centre, summon-only) ·
// DO = task list (right). Signals are noticed/dismissed; only tasks get "done"; the one bridge
// is the make-task button. ActionCenter dissolved into the NOTICE (alerts) + DO (tasks) halves.
// Create flows are kept as header actions. ponytail: natural-height body inside <Layout>'s main.
const DashboardPage: React.FC = () => {
  // Discovers live sessions from backend state (not only from the state-change event), so
  // opening the dashboard mid-meeting still surfaces one deduplicated tile per session.
  useLiveMeetingSurfacing()
  const { t } = useTranslation('dashboard')
  const { account, user } = useAccount()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [isCreatingTask, setIsCreatingTask] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // TASK-600/650: the NOTICE/DO rails are hideable — they occupy canvas space.
  // Preference persists (localStorage); restore controls live in the stable
  // header toolbar, so a hidden rail leaves NO placeholder strip in the body.
  const [radarVisible, toggleRadar] = usePersistentToggle('kielis-rail-radar', true)
  const [doVisible, toggleDo] = usePersistentToggle('kielis-rail-do', true)
  const railVisible = { radar: radarVisible, do: doVisible }
  const toggleRail = (key: 'radar' | 'do') => (key === 'radar' ? toggleRadar() : toggleDo())

  // TASK-924: the desk lens. Owned HERE for the same reason the rail toggles are — it spans
  // both rails, and two components each holding their own copy would let the chip row and the
  // Do column disagree about which desk you are at. One state, passed down.
  const deskLens = useDeskLens()

  // Dev-only summon proves widgets render real data end-to-end (TASK-494 AC);
  // replaced by the radar Swap/Email buttons (TASK-498). Client-scoped widgets get
  // only the first client's id; book/compliance widgets take nothing.
  const addSpecs = useCanvasStore((s) => s.addSpecs)
  const DEV_CLIENT_WIDGETS = [
    'DnaCard', 'NotesTab', 'DocumentsTab', 'MinIODocumentsSection',
    'PortfoliosTab', 'ProposalsList', 'CockpitPortfolioView',
    // TASK-496 SixEase-only ports (all client-scoped: mount with { clientId })
    'MeetingPrep', 'DriftBars', 'AllocationDonut', 'ConflictsList',
    'Client360', 'SectorTreemap', 'EmailDraft', 'BeforeAfter',
    // TASK-507 expanded self-fetch set (client-scoped)
    'SecureLinksTable', 'DocumentRequestList',
    // TASK-512 client scope (VoiceNoteWidget/KYCTab/RiskProfileTab read clientId + clientName)
    'VoiceNoteWidget', 'KYCTab', 'RiskProfileTab',
    // TASK-681 epic (review fix): this list had gone stale at TASK-512, so none of the epic's
    // widgets appeared here and the button read as "these are all the widgets" while showing
    // two-thirds of them. Keep it current when a widget is registered.
    'ClientReaction', 'RiskTimeline', 'KnowledgeGaps', 'CountryExposure',
  ]
  const DEV_BOOK_WIDGETS = [
    'COReviewQueue', 'DocumentComplianceDashboard', 'QuarantineQueue', 'RegulatoryFeedTab',
    // TASK-507 expanded self-fetch set (book scope, no props)
    'AllDocumentsView', 'DocumentAnalysisTab',
    // TASK-512 book scope (no props)
    'AIUsageStats',
    // TASK-685 (review fix)
    'TasksAwaitingReview',
  ]
  const summonAllProof = async () => {
    // Authenticated gateway route (Bearer + X-Account-ID via httpClient interceptors);
    // the legacy realClientStore/apiService raw-fetches /clients with no auth → empty.
    // ClientListResponse keys each client by `id` (the client UUID), not `client_id`.
    const { clients } = await httpClient.get<{ clients: { id: string; name?: string }[] }>('/api/clients')
    // Prefer the hero persona (Schneider) — the only seeded client with non-empty
    // conflicts/swaps (TASK-497), so fit-based widgets render with data, not empty.
    const hero = clients?.find((c) => c.name?.toLowerCase().includes('schneider'))
    const id = hero?.id ?? clients?.[0]?.id
    const name = hero?.name ?? clients?.[0]?.name
    if (!id) toast.error('No clients on this account to summon client-scoped widgets')
    addSpecs([
      // clientName mirrors the server-injected client-tier prop (TASK-512) so KYC/Risk
      // tabs render as they would from a real summon.
      ...(id ? DEV_CLIENT_WIDGETS.map((component) => ({ component, props: { clientId: id, clientName: name } })) : []),
      ...DEV_BOOK_WIDGETS.map((component) => ({ component, props: {} })),
    ])
  }

  const formatGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return t('greeting.morning')
    if (hour < 17) return t('greeting.afternoon')
    return t('greeting.evening')
  }

  const handleCreateMeeting = async (meetingData: MeetingCreationData) => {
    setIsCreatingMeeting(true)
    setError(null)
    try {
      // TASK-671: this entry point discarded the response and never opened preparation, so
      // creating from the dashboard header left the RM on a bare dashboard. `session_id` is
      // top-level (httpClient already unwraps `.data`); open THAT session's preparation.
      const result = await httpClient.post<{ session_id?: string }>('/meeting/create', meetingData)
      setIsCreateModalOpen(false)
      toast.success(t('buttons.new_meeting'))
      if (result?.session_id) {
        addSpecs([
          {
            component: 'MeetingPrep',
            props: { sessionId: result.session_id, clientId: meetingData.client_id },
          },
        ])
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || t('errors.create_failed')
      setError(message)
    } finally {
      setIsCreatingMeeting(false)
    }
  }

  const handleCreateTask = async (taskData: TaskCreateRequest) => {
    setIsCreatingTask(true)
    try {
      await taskService.createTask(taskData)
      toast.success('Task created successfully')
      setIsTaskModalOpen(false)
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to create task'
      toast.error(message)
    } finally {
      setIsCreatingTask(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <SecondaryNav />

      {/* Workbench header strip: greeting + create actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-6 lg:px-8">
        <h2 className="text-lg font-semibold text-gray-900">
          {formatGreeting()}, {user?.firstName || account?.name || 'User'}
        </h2>
        <div className="flex items-center gap-3">
          {/* TASK-650: stable rail toggles — the ONLY restore affordance; hidden
              rails leave no placeholder strip in the body. */}
          <div className="flex items-center gap-1" role="group" aria-label="Panel visibility">
            <button
              type="button"
              onClick={() => toggleRail('radar')}
              aria-pressed={railVisible.radar}
              aria-label={railVisible.radar ? 'Hide signals rail' : 'Show signals rail'}
              title={railVisible.radar ? 'Hide signals rail' : 'Show signals rail'}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                railVisible.radar
                  ? 'border-gray-300 bg-gray-100 text-gray-800'
                  : 'border-gray-200 bg-white text-gray-500 hover:text-gray-800'
              }`}
            >
              <Radar className="h-3.5 w-3.5" />
              Signals
            </button>
            <button
              type="button"
              onClick={() => toggleRail('do')}
              aria-pressed={railVisible.do}
              aria-label={railVisible.do ? 'Hide action rail' : 'Show action rail'}
              title={railVisible.do ? 'Hide action rail' : 'Show action rail'}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                railVisible.do
                  ? 'border-gray-300 bg-gray-100 text-gray-800'
                  : 'border-gray-200 bg-white text-gray-500 hover:text-gray-800'
              }`}
            >
              <ListChecks className="h-3.5 w-3.5" />
              Do
            </button>
          </div>
          {/* Dev-server builds always carry it; a production build opts in with
              VITE_SHOW_DEV_SUMMON=true (the demo server does — it is the only way to
              put every widget on the canvas without going through the chat brain). */}
          {(import.meta.env.DEV || import.meta.env.VITE_SHOW_DEV_SUMMON === 'true') && (
            <button
              type="button"
              onClick={summonAllProof}
              className="rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              + Summon all widgets (dev)
            </button>
          )}
          <button
            onClick={() => setIsTaskModalOpen(true)}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <ListTodo className="mr-2 h-4 w-4" />
            {t('buttons.new_task', 'Add Task')}
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('buttons.new_meeting')}
          </button>
        </div>
      </div>

      {/* TASK-630: zero-client first-run guidance; renders null once a client exists */}
      <FirstRunCard />

      {error && (
        <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 p-3 sm:mx-6 lg:mx-8">
          <div className="flex items-center">
            <span className="mr-2 text-red-400">⚠️</span>
            <p className="flex-1 text-sm text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* TASK-650 focused workstream: SIGNALS (compact rail) · CANVAS (dominant) ·
          DO (compact rail). Three columns from xl (1280) with 280px rails so the
          canvas is the largest visible region at 1280×720 (TASK-601's starvation
          came from 340px rails; 280px leaves the canvas ~640px). Below xl the
          layout is an explicit stack with the CANVAS FIRST — signals and tasks
          follow the work, not the other way around. Hidden rails render nothing
          here (restore lives in the header toolbar). Bottom padding clears the
          fixed dock so it never overlaps content. */}
      <div className="flex flex-col xl:flex-row flex-1 gap-4 p-4 pb-44">
        <section aria-label="Canvas workstream" className="order-first min-w-0 flex-1 min-h-[300px] xl:order-none">
          <Canvas />
        </section>
        {/* TASK-749: `xl:overflow-auto` with no bound never engages — the rail grows to fit
            and the PAGE scrolls instead, so the empty dashboard was 2.7 viewports tall and
            got taller with every news item and alert. The cap makes the height a property of
            the LAYOUT rather than of how much signal the account happens to have; content is
            not clipped, it scrolls inside its own column and stays keyboard-reachable.
            Only from xl: below that the layout is an explicit stack (canvas first) where
            growing is the correct behaviour and a capped rail would trap content. */}
        {railVisible.radar && (
          <aside
            aria-label="Signals"
            /*
              TASK-844: the Radar rail is wider than the Do rail, deliberately — they were
              the same `280/320px` and they do not carry the same load. A task card is a
              title and a chip; a Radar row is a title, a client, a CHF figure, an obligation
              band, an SLA class, a breach age and a source tag. At 320px that row spent 57px
              on the title and 128px on two fixed badge columns, wrapped into a 132px-tall
              block, and still truncated to "Re-profil…".
              Restructuring the row (see `ChangeRadar`) made it legible; this makes it fit.
              At 1600px the canvas keeps ~900px with the rail at 420px, so the working
              surface is not starved to pay for it.
            */
            className="w-full shrink-0 xl:order-first xl:w-[340px] 2xl:w-[420px] xl:max-h-[calc(100vh-14rem)] xl:overflow-y-auto"
          >
            {/* TASK-611: overnight book triage lives on the ONE attention surface,
                stacked above the radar — no new route/tab. */}
            <div className="mb-4">
              <MorningDigest />
            </div>
            <ChangeRadar onHide={() => toggleRail('radar')} deskLens={deskLens} />
          </aside>
        )}
        {/* TASK-924: the lens goes with its control. The chip row lives in the Signals rail, so
            the Do column is lensed only while that rail is BOTH un-hidden and actually on screen
            (it renders nothing when the book is quiet). Otherwise the filter persists with no
            visible way back to `all`, and a reload would not clear it either. */}
        {railVisible.do && (
          <DoColumn
            onHide={() => toggleRail('do')}
            deskLens={railVisible.radar && deskLens.controllable ? deskLens : undefined}
          />
        )}
      </div>

      {/* TASK-499/712: the single bottom input dock (fixed) — dock turns over /ws/chat. */}
      <InputDock />

      {/* TASK-714: popup workspaces — summoned above the dimmed canvas, never by
          navigating away; the topmost popup round-trips through ?focus=. */}
      <FocusOverlayHost />

      <CreateMeetingModal
        isOpen={isCreateModalOpen}
        onClose={() => !isCreatingMeeting && setIsCreateModalOpen(false)}
        onCreateMeeting={handleCreateMeeting}
        isLoading={isCreatingMeeting}
      />
      <AddTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => !isCreatingTask && setIsTaskModalOpen(false)}
        onSubmit={handleCreateTask}
        isLoading={isCreatingTask}
      />
    </div>
  )
}

export default DashboardPage;
